import React, { useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatDateDMY } from '../../../../../utils/dateUtils';
import Autocomplete from '@mui/material/Autocomplete';
import { ReactComponent as DeleteIcon } from '../../../../../assets/SVG/trash.svg';
import { PharmacyItem as FormPharmacyItem, PackageDetail, calculateFromInclusiveMRP } from '../newPharmacyForm.helper';
import { CHECKED_ICON, UNCHECKED_ICON } from '../../common/InvoiceTableCommon';
import useCurrency from '../../../../../components/CurrencyProvider';
import {
  TEST_IDS,
  ARIA_LABELS,
  getItemRowTestIds,
  PREGNANCY_CLASSES,
  TABLE_SCROLLBAR_STYLES,
  TABLE_HEADER_ROW_STYLES,
  INPUT_FIELD_STYLES,
  SELECT_FIELD_STYLES,
} from './pharmacyItemsTable.constants';

// UOM conversion type for items that include unit conversions
interface UomConversion {
  id: number;
  uom_code: string;
  converted_mrp?: number;
  converted_cost_price?: number;
}

type ItemWithUom = FormPharmacyItem & { uomConversions?: UomConversion[] };

export interface PharmacyItemsTableProps {
  items: FormPharmacyItem[];
  onSelectChange: (index: number) => void;
  onRemoveItem?: (index: number) => void;
  onFieldChange: (index: number, field: keyof FormPharmacyItem, value: string | number) => void;
  /** Batch update for multiple fields at once - use this for calculated fields */
  onItemUpdate?: (index: number, updates: Partial<FormPharmacyItem>) => void;
  errors?: Array<Record<string, string>>;
  touched?: Array<Record<string, boolean>>;
  /** If true, shows additional columns for billing (MRP, concession, tax, net amount) */
  showBillingColumns?: boolean;
  /** If true, shows delete action column */
  showDeleteAction?: boolean;
  /** If true, shows read-only mode without editable fields */
  readOnly?: boolean;
  /** Invoice status - if "dispensed", allows editing pregnancy class */
  invoiceStatus?: string;
  /** Form-level package ID - if set, disables concession fields for all items */
  formPackageId?: string | null;
  /** If true, shows table header row. Defaults to true */
  showHeader?: boolean;
  /** List of provider names for the provider dropdown */
  providers?: string[];
  /** Callback when package selection changes for a line item */
  onPackageChange?: (index: number, packageDetail: PackageDetail | null) => void;
}

const PharmacyItemsTable: React.FC<PharmacyItemsTableProps> = ({
  items,
  onSelectChange,
  onRemoveItem,
  onFieldChange,
  onItemUpdate,
  errors = [],
  touched = [],
  showBillingColumns = true,
  showDeleteAction = true,
  readOnly = false,
  invoiceStatus = '',
  showHeader = true,
  formPackageId = null,
  onPackageChange,
}) => {
  const { currencySymbol } = useCurrency();
  const { t } = useTranslation('billing_config');
  
  // Set default UOM and price fields for readOnly mode only once using useEffect
  useEffect(() => {
    
    if (readOnly && onItemUpdate) {
      items.forEach((item, index) => {
        // DON'T override baseMRP and baseCostPrice with UOM conversions
        // These should come from the batch data and should be preserved
        // Only update totalMRPAmount based on the existing baseMRP and billedQty
        if (item.baseMRP && item.baseMRP > 0) {
          const newTotalMRP = item.baseMRP * (item.billedQty || 0);
          if (item.totalMRPAmount !== newTotalMRP) {
            onItemUpdate(index, {
              totalMRPAmount: newTotalMRP,
            });
          }
        }
      });
    }
  }, [readOnly, items, onItemUpdate]);

  /**
   * Get field error for a specific item index and field
   */
  const getFieldError = useCallback((index: number, field: string) => {
    return touched[index]?.[field] && errors[index]?.[field];
  }, [touched, errors]);

  /**
   * Clamp a value to be non-negative and not exceed a maximum
   * Used to prevent concession amount from exceeding total MRP
   */
  const clampValue = useCallback((value: number, max: number): number => {
    // If there is no positive MRP, no positive concession should be allowed
    if (max <= 0) return 0;
    if (value < 0) return 0;
    if (value > max) return max;
    return value;
  }, []);

  /**
   * Clamp percentage to valid range (0-100)
   */
  const clampPercent = useCallback((percent: number): number => {
    if (percent < 0) return 0;
    if (percent > 100) return 100;
    return percent;
  }, []);

  /**
   * Helper function for calculating concession values using taxes array
   * Includes clamping to ensure concession doesn't exceed total MRP
   */
  const calculateConcessionValues = useCallback((
    totalMRP: number,
    concessionAmount: number,
    concessionPercent: number,
    taxes: Array<{ name: string; percent: number; amount: number }>,
    mode: 'amount' | 'percent'
  ) => {
    let newAmount = concessionAmount;
    let newPercent = concessionPercent;
    if (mode === 'amount') {
      // Clamp amount to not exceed total MRP
      newAmount = clampValue(concessionAmount, totalMRP);
      newPercent = totalMRP > 0 ? (newAmount / totalMRP) * 100 : 0;
    } else {
      // Clamp percent to 0-100 range
      newPercent = clampPercent(concessionPercent);
      newAmount = (totalMRP * newPercent) / 100;
      // Ensure calculated amount doesn't exceed MRP (edge case)
      newAmount = clampValue(newAmount, totalMRP);
    }
    const totalTaxAmount = taxes.reduce((sum, taxItem) => sum + (taxItem.amount || 0), 0);
    const netAmount = totalMRP - newAmount + totalTaxAmount;
    return { concessionAmount: newAmount, concessionPercent: newPercent, netAmount };
  }, [clampValue, clampPercent]);

  // Empty state
  if (items.length === 0) {
    return (
      <Box
        sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 1, textAlign: 'center' }}
        data-testid={TEST_IDS.emptyState}
        role="status"
        aria-label={ARIA_LABELS.emptyState}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('pharmacy_items_table.empty_state')}
        </Typography>
      </Box>
    );
  }

  // Show Package column when any item has package_details or is already from a package
  const showPackageColumn = items.some(item => (item.package_details && item.package_details.length > 0) || item.is_fromPackage || item.packageId);

  return (
    <TableContainer
      sx={TABLE_SCROLLBAR_STYLES}
      data-testid={TEST_IDS.tableContainer}
      aria-label={ARIA_LABELS.tableContainer}
    >
      <Table
        sx={{ borderCollapse: 'collapse', minWidth: showBillingColumns ? (showPackageColumn ? 2270 : 2020) : 1540 }}
        data-testid={TEST_IDS.table}
      >
        {showHeader && (
          <TableHead data-testid={TEST_IDS.tableHeader}>
            <TableRow sx={TABLE_HEADER_ROW_STYLES}>
              <TableCell padding="checkbox" sx={{ borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }} />
              <TableCell sx={{ width: 100 }}>{t('pharmacy_items_table.headers.visit_date')}</TableCell>
              <TableCell sx={{ width: 200 }}>{t('pharmacy_items_table.headers.item_code_name')}</TableCell>
              {showPackageColumn && (
                <TableCell sx={{ width: 250, minWidth: 250 }}>{t('pharmacy_items_table.headers.package', { defaultValue: 'Package' })}</TableCell>
              )}
              <TableCell sx={{ width: 180 }}>{t('pharmacy_items_table.headers.batch_expiry')}</TableCell>
              <TableCell sx={{ width: 120 }}>{t('pharmacy_items_table.headers.manufacturer')}</TableCell>
              <TableCell sx={{ width: 140 }}>{t('pharmacy_items_table.headers.available_qty_uom')}</TableCell>
              <TableCell sx={{ width: 220 }}>{t('pharmacy_items_table.headers.prescribed_strength_qty_uom')}</TableCell>
              <TableCell sx={{ width: 220 }}>{t('pharmacy_items_table.headers.billed_strength_qty_uom')}</TableCell>
              <TableCell sx={{ width: 180 }}>{t('pharmacy_items_table.headers.rack_shelf_container')}</TableCell>
              {showBillingColumns && (
                <>
                  <TableCell sx={{ width: 300 }}>{t('pharmacy_items_table.headers.mrp_price')}</TableCell>
                  <TableCell sx={{ width: 200 }}>{t('pharmacy_items_table.headers.concession_amount_percent')}</TableCell>
                  <TableCell sx={{ width: 200 }}>{t('pharmacy_items_table.headers.vat_amount_percent')}</TableCell>
                  <TableCell sx={{ width: 120 }}>{t('pharmacy_items_table.headers.net_amount')}</TableCell>
                  <TableCell sx={{ width: 150 }}>{t('pharmacy_items_table.headers.pregnancy_class')}</TableCell>
                  <TableCell sx={{ width: 150 }}>{t('pharmacy_items_table.headers.provider')}</TableCell>
                </>
              )}
              {showDeleteAction && (
                <TableCell sx={{ borderTopRightRadius: 12, borderBottomRightRadius: 12 }} />
              )}
            </TableRow>
          </TableHead>
        )}

        <TableBody data-testid={TEST_IDS.tableBody}>
          {items.map((item, index) => {
            const testIds = getItemRowTestIds(item.itemCode);
            return(
              <TableRow
                key={item.itemCode ? `${item.itemCode}-${item.batchCode || index}` : index}
                data-testid={testIds.row}
                sx={{
                  background: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: '#F9FAFB',
                  },
                  '& td': {
                    borderTop: index === 0 ? 'none' : undefined,
                    borderBottom: index === items.length - 1 ? 'none' : undefined,
                    paddingY: 1.25,
                  },
                }}
              >
                {/* Checkbox */}
                <TableCell padding="checkbox" sx={{ borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }}>
                  <Checkbox
                    checked={item.checked}
                    onChange={() => !readOnly && onSelectChange(index)}
                    icon={UNCHECKED_ICON}
                    checkedIcon={CHECKED_ICON}
                    disableRipple
                    disabled={readOnly}
                    inputProps={{
                      'aria-label': ARIA_LABELS.itemCheckbox(item.itemName || item.itemCode),
                    }}
                    data-testid={testIds.checkbox}
                  />
                </TableCell>

                {/* Visit Date */}
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2">{formatDateDMY(item.visitDate)}</Typography>
                </TableCell>

                {/* Item Code / Name */}
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {(item.prescriptionId ?? 0) > 0 && (
                        <Box component="span" sx={{ color: 'error.main', fontWeight: 500 }}>
                          {t('pharmacy_items_table.labels.rx_indicator')}
                          {' '}
                        </Box>
                      )}
                      {t('pharmacy_items_table.labels.item_code')}: {item.itemCode}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {item.itemName}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Package Selection */}
                {showPackageColumn && (
                  <TableCell sx={{ width: 250, minWidth: 250 }}>
                    {readOnly ? (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.packageName || '-'}
                      </Typography>
                    ) : item.package_details && item.package_details.length > 0 ? (
                      <Autocomplete
                        size="small"
                        options={item.package_details}
                        value={item.package_details.find(pkg => pkg.package_id === item.packageId) || null}
                        onChange={(_event, newValue) => {
                          if (onPackageChange) {
                            onPackageChange(index, newValue);
                          } else if (onItemUpdate) {
                            onItemUpdate(index, {
                              packageId: newValue ? newValue.package_id : null,
                              packageName: newValue?.package_name || undefined,
                              is_fromPackage: !!newValue,
                              package_pharmacy_item_id: newValue?.package_pharmacy_item_id ?? undefined,
                              package_pending_qty: newValue?.quantity_remaining ?? undefined,
                              package_total_qty: newValue?.total_quantity ?? undefined,
                              uom_description: newValue?.uom_description ?? undefined,
                            });
                          }
                        }}
                        getOptionLabel={(option) => option.package_name}
                        isOptionEqualToValue={(option, value) => option.package_id === value.package_id}
                        renderOption={(props, option) => (
                          <Tooltip title={option.package_name} arrow placement="right">
                            <li {...props}>{option.package_name}</li>
                          </Tooltip>
                        )}
                        renderInput={(params) => (
                          <Tooltip title={item.packageName || ''} arrow placement="top">
                            <TextField
                              {...params}
                              placeholder={t('pharmacy_items_table.headers.package', { defaultValue: 'Select Package' })}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 3,
                                  '& fieldset': { borderColor: '#E4E7EC' },
                                  '&:hover fieldset': { borderColor: '#D0D5DD' },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#5A8AEA',
                                    boxShadow: '0 0 0 1px rgba(90, 138, 234, 0.15)',
                                  },
                                },
                              }}
                            />
                          </Tooltip>
                        )}
                        sx={{ minWidth: 220 }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {item.packageName || '-'}
                      </Typography>
                    )}
                  </TableCell>
                )}

                {/* Batch Code / Expiry Date */}
                <TableCell>
                  <Box>
                    {!readOnly && item.checked && item.availableBatches?.length === 0 && (
                      <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 0.5, fontWeight: 500 }}>
                        No batches available
                      </Typography>
                    )}
                    <FormControl fullWidth size="small" sx={{ mb: 0.5 }} error={!!getFieldError(index, 'batchCode')}>
                      <Select
                        value={item.batchCode}
                        error={!!getFieldError(index, 'batchCode')}
                        onChange={(e) => {
                          if (readOnly) return;
                          const selectedBatchCode = e.target.value;
                          const selectedBatch = item.availableBatches?.find(
                            (b) => b.batchNumber === selectedBatchCode
                          );
                        
                          // Use batch update if available
                          if (onItemUpdate && selectedBatch) {
                            const newBaseMRP = selectedBatch.baseMrp || 0;
                            const newBaseCostPrice = selectedBatch.baseCostPrice || 0;
                            const newTotalMRP = newBaseMRP * item.billedQty;
                            // Use batch-level taxes if available, fallback to item-level
                            const batchTaxes = selectedBatch.taxes && selectedBatch.taxes.length > 0
                              ? selectedBatch.taxes
                              : (item.taxes || []);
                            const taxCalc = calculateFromInclusiveMRP(newBaseMRP, item.billedQty, batchTaxes);
                          
                            onItemUpdate(index, {
                              batchCode: selectedBatchCode,
                              batchId: selectedBatch.batchId,
                              expiryDate: selectedBatch.expiryDate,
                              baseMRP: newBaseMRP,
                              baseCostPrice: newBaseCostPrice,
                              totalMRPAmount: newTotalMRP,
                              sellingPricePerUnit: taxCalc.sellingPricePerUnit,
                              totalSellingPrice: taxCalc.totalSellingPrice,
                              taxes: taxCalc.taxesWithAmounts,
                              netAmount: taxCalc.netAmount - (item.concessionAmount || 0),
                              storeId: selectedBatch.storeId,
                            });
                          } else {
                            onFieldChange(index, 'batchCode', e.target.value);
                            if (selectedBatch) {
                              onFieldChange(index, 'expiryDate', selectedBatch.expiryDate);
                            }
                          }
                        }}
                        displayEmpty
                        data-testid={testIds.batchSelect}
                        inputProps={{
                          'aria-label': ARIA_LABELS.batchCodeSelect(item.itemName || item.itemCode),
                        }}
                        renderValue={(selected) => {
                          if (!selected) {
                            return <Typography sx={{ color: 'text.disabled' }}>{t('pharmacy_items_table.labels.select_batch_code')}</Typography>;
                          }
                          return selected;
                        }}
                        sx={SELECT_FIELD_STYLES}
                        disabled={readOnly}
                      >
                        {item.availableBatches?.length > 0 ? (
                          item.availableBatches.map((batch) => (
                            <MenuItem 
                              key={batch.batchNumber} 
                              value={batch.batchNumber}
                              disabled={batch.isExpired}
                              sx={{
                                color: batch.isExpired ? 'error.main' : 'inherit',
                                opacity: batch.isExpired ? 0.6 : 1,
                              }}
                            >
                              {batch.batchNumber}
                              {batch.isExpired && ` (${t('pharmacy_items_table.labels.expired')})`}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem value="" disabled>
                            <Typography sx={{ color: 'text.disabled' }}>{t('pharmacy_items_table.labels.no_batches_available')}</Typography>
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    {getFieldError(index, 'batchCode') && (
                      <FormHelperText error sx={{ mx: 0, mt: 0.25 }}>
                        {getFieldError(index, 'batchCode')}
                      </FormHelperText>
                    )}
                    {item.expiryDate && (
                      <Typography variant="caption" color="text.secondary">
                        {t('pharmacy_items_table.labels.expiry_date')}: {item.expiryDate}
                      </Typography>
                    )}
                  </Box>
                </TableCell>

                {/* Manufacturer */}
                <TableCell>
                  <Typography variant="body2">{item.manufacturer || '-'}</Typography>
                </TableCell>

                {/* Available Qty / UOM */}
                <TableCell>
                  {(() => {
                    const stock = item.availableStock;
                    const bqty = Number(item.billedQty) || 0;
                    const isOverStock = !readOnly && stock !== undefined && stock !== null && bqty > stock;
                    const isLowStock = !readOnly && stock !== undefined && stock !== null && stock > 0 && bqty >= stock;
                    const isZeroStock = !readOnly && stock === 0;
                    const color = (item.checked && (isOverStock || isZeroStock)) ? 'error.main' : (item.checked && isLowStock) ? 'warning.main' : 'text.primary';
                    return (
                      <Typography variant="body2" sx={{ fontWeight: 500, color }}>
                        {stock !== undefined && stock !== null ? `${stock} ${item.availableUom || ''}` : '-'}
                        {item.checked && isZeroStock && (
                          <Typography variant="caption" color="error.main" display="block">Out of stock</Typography>
                        )}
                      </Typography>
                    );
                  })()}
                </TableCell>

                {/* Prescribed Strength / Qty / UOM - READ ONLY DISPLAY */}
                {/* Only show for actual Rx or package items; hide for manually added items */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {((item.prescriptionId ?? 0) > 0 || item.is_fromPackage || item.packageId) &&
                    item.prescribedStrength && item.prescribedQty
                      ? `${item.prescribedStrength} / ${item.prescribedQty} ${item.prescribedUOM}`
                      : '-'}
                  </Typography>
                </TableCell>

                {/* Billed Strength / Qty / UOM - EDITABLE */}
                <TableCell>
                  <>
                    <Box sx={{ display: 'flex', gap: 0.75, mb: 0.75, alignItems: 'center' }}>
                      {/* Frequency: read-only for prescribed (rx) items, hidden for non-prescribed */}
                      {(item.prescriptionId ?? 0) > 0 ? (
                        <Typography variant="body2" sx={{ minWidth: 80, fontWeight: 500 }}>
                          {item.frequency || '-'}
                        </Typography>
                      ) : (
                        <Typography variant="body2" sx={{ minWidth: 80, color: 'text.secondary' }}>
                          -
                        </Typography>
                      )}

                      {(() => {
                        const billedQtyNum = Number(item.billedQty) || 0;
                        const availQty = item.availableStock ?? Infinity;
                        const rxRemaining = Number(item.prescribed_remaining_qty) || Number(item.prescribedQty) || Infinity;
                        // Only enforce Rx remaining limit for actual prescription items (not manually added / package items)
                        const isPrescribedItem = (item.prescriptionId ?? 0) > 0 && !item.is_fromPackage && !item.packageId;
                        const overStock = !readOnly && billedQtyNum > availQty;
                        const overPrescribed = !readOnly && isPrescribedItem && billedQtyNum > rxRemaining && rxRemaining !== Infinity;
                        let qtyHelperText = getFieldError(index, 'billedQty') || '';
                        if (overStock) qtyHelperText = `Max available: ${item.availableStock}`;
                        else if (overPrescribed) qtyHelperText = `Max Rx rem.: ${rxRemaining}`;
                        return (
                          <TextField
                            size="small"
                            label={t('pharmacy_items_table.labels.qty')}
                            type="number"
                            value={item.billedQty || ''}
                            onKeyDown={(e) => {
                              // Block non-numeric characters: +, -, e, E, . (we only want positive integers)
                              if (['+', '-', 'e', 'E', '.'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onChange={(e) => {
                              if (readOnly) return;
                              const inputQty = Number(e.target.value) || 0;
                              const maxQty = item.availableStock ?? Infinity;
                              // Clamp to [0, availableStock]; negative values are floored to 0
                              const newQty = Math.max(0, Math.min(inputQty, maxQty));
                              const newTotalMRP = item.baseMRP * newQty;
                              const taxCalc = calculateFromInclusiveMRP(item.baseMRP, newQty, item.taxes || []);
                              if (onItemUpdate) {
                                onItemUpdate(index, {
                                  billedQty: newQty,
                                  totalMRPAmount: newTotalMRP,
                                  sellingPricePerUnit: taxCalc.sellingPricePerUnit,
                                  totalSellingPrice: taxCalc.totalSellingPrice,
                                  taxes: taxCalc.taxesWithAmounts,
                                  netAmount: taxCalc.netAmount - (item.concessionAmount || 0),
                                });
                              } else {
                                onFieldChange(index, 'billedQty', newQty);
                              }
                            }}
                            placeholder={t('pharmacy_items_table.placeholders.qty')}
                            error={!!getFieldError(index, 'billedQty') || overStock || overPrescribed}
                            helperText={qtyHelperText}
                            sx={{ width: 100, ...INPUT_FIELD_STYLES, '& .MuiFormHelperText-root': { textAlign: 'center', margin: 0, fontSize: 10 } }}
                            slotProps={{ htmlInput: { min: 0, max: item.availableStock ?? undefined, step: 1 } }}
                            disabled={readOnly}
                            data-testid={testIds.qtyInput}
                            inputProps={{
                              'aria-label': ARIA_LABELS.quantityInput(item.itemName || item.itemCode),
                            }}
                          />
                        );
                      })()}
                    </Box>

                    <FormControl fullWidth size="small" error={!!getFieldError(index, 'billedUOM')}>
                      <Select
                        value={item.billedUOM}
                        onChange={(e) => {
                          if (readOnly) return;
                          const selectedUomCode = e.target.value as string;
                          const itemWithUom = item as ItemWithUom;
                          const selectedUom = itemWithUom.uomConversions?.find(
                            (uom) => uom.uom_code === selectedUomCode
                          );

                          if (onItemUpdate && selectedUom) {
                          // Update UOM and recalculate prices based on conversion
                            const convertedMRP = selectedUom.converted_mrp || 0;
                            const convertedCost = selectedUom.converted_cost_price || 0;
                            const newTotalMRP = convertedMRP * (item.billedQty || 0);
                            onItemUpdate(index, {
                              billedUOM: selectedUomCode,
                              baseMRP: convertedMRP,
                              baseCostPrice: convertedCost,
                              totalMRPAmount: newTotalMRP,
                            });
                          } else {
                            onFieldChange(index, 'billedUOM', selectedUomCode);
                          }
                        }}
                        error={!!getFieldError(index, 'billedUOM')}
                        disabled={readOnly || !item.batchCode}
                        displayEmpty
                        data-testid={testIds.uomSelect}
                        inputProps={{
                          'aria-label': ARIA_LABELS.billedUomSelect(item.itemName || item.itemCode),
                        }}
                        renderValue={(selected) => {
                          if (!selected) {
                            return <Typography sx={{ color: 'text.disabled' }}>{t('pharmacy_items_table.labels.select_uom')}</Typography>;
                          }
                          return selected;
                        }}
                        sx={SELECT_FIELD_STYLES}
                      >
                        {(item as ItemWithUom).uomConversions && (item as ItemWithUom).uomConversions!.length > 0 ? (
                        (item as ItemWithUom).uomConversions!.map((uom) => (
                          <MenuItem key={uom.id} value={uom.uom_code}>
                            {uom.uom_code}
                          </MenuItem>
                        ))
                        ) : (
                          <MenuItem value="" disabled>
                            <Typography sx={{ color: 'text.disabled' }}>{t('pharmacy_items_table.labels.no_uom_available')}</Typography>
                          </MenuItem>
                        )}
                      </Select>
                      {getFieldError(index, 'billedUOM') && (
                        <FormHelperText error sx={{ mx: 0, mt: 0.25 }}>
                          {getFieldError(index, 'billedUOM')}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </>
                </TableCell>

                {/* Rack / Shelf / Container */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: 13 }}>
                    <Box component="span" sx={{ color: 'text.secondary' }}>{t('pharmacy_items_table.labels.rack')}:</Box> {item.rack || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 13, mt: 0.25 }}>
                    <Box component="span" sx={{ color: 'text.secondary' }}>{t('pharmacy_items_table.labels.shelf')}:</Box> {item.shelf || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 13, mt: 0.25 }}>
                    <Box component="span" sx={{ color: 'text.secondary' }}>{t('pharmacy_items_table.labels.container')}:</Box> {item.container || '-'}
                  </Typography>
                </TableCell>

                {/* Billing Columns */}
                {showBillingColumns && (
                  <>
                    {/* MRP Price */}
                    <TableCell sx={{ width: 200, minWidth: 200 }}>
                      {!readOnly && item.checked && item.batchCode && item.baseMRP === 0 && (
                        <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 0.5, fontWeight: 500 }}>
                          MRP not set for selected batch
                        </Typography>
                      )}
                      <Typography 
                        variant="body2" 
                        display="block" 
                        sx={{ fontSize: 13, fontWeight: 400, color: '#667085' }}
                      >
                        {t('pharmacy_items_table.labels.base_mrp')}: {currencySymbol}{item.baseMRP.toFixed(2)}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        display="block" 
                        sx={{ fontSize: 13, fontWeight: 400, color: '#667085' }}
                      >
                        {t('pharmacy_items_table.labels.base_cost')}: {currencySymbol}{(item.baseCostPrice || 0).toFixed(2)}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500, 
                          color: '#101828', 
                          fontSize: 14, 
                          mt: 0.5 
                        }}
                      >
                        {t('pharmacy_items_table.labels.total_mrp')}: {currencySymbol}{item.totalMRPAmount.toFixed(2)}
                      </Typography>
                    </TableCell>

                    {/* Concession Amount / (%) */}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                        <TextField
                          size="small"
                          type="number"
                          disabled={!!formPackageId || item.is_fromPackage || (item.packageId !== null && item.packageId !== undefined) || item.is_from_dispense}
                          value={typeof item.concessionAmount === 'number' ? item.concessionAmount : 0}
                          onChange={(e) => {
                            const amount = Number(e.target.value) || 0;
                            const calcResult = calculateConcessionValues(
                              item.totalMRPAmount,
                              amount,
                              item.concessionPercent || 0,
                              item.taxes || [],
                              'amount'
                            );
                          
                            // Use batch update if available
                            if (onItemUpdate) {
                              onItemUpdate(index, calcResult);
                            } else {
                              onFieldChange(index, 'concessionAmount', calcResult.concessionAmount);
                              onFieldChange(index, 'concessionPercent', calcResult.concessionPercent);
                              onFieldChange(index, 'netAmount', calcResult.netAmount);
                            }
                          }}
                          placeholder={t('pharmacy_items_table.placeholders.amount')}
                          data-testid={testIds.concessionAmountInput}
                          inputProps={{
                            'aria-label': ARIA_LABELS.concessionAmountInput(item.itemName || item.itemCode),
                          }}
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">{currencySymbol}</InputAdornment>
                              ),
                            },
                            htmlInput: { min: 0, step: 0.01 },
                          }}
                          sx={{
                            flex: '0 0 125px',
                            maxWidth: 130,
                            ...INPUT_FIELD_STYLES,
                          }}
                        />
                        <TextField
                          size="small"
                          type="number"
                          disabled={!!formPackageId || item.is_fromPackage || (item.packageId !== null && item.packageId !== undefined) || item.is_from_dispense}
                          value={item.concessionPercent || ''}
                          onChange={(e) => {
                            const percent = Number(e.target.value) || 0;
                            const calcResult = calculateConcessionValues(
                              item.totalMRPAmount,
                              item.concessionAmount || 0,
                              percent,
                              item.taxes || [],
                              'percent'
                            );
                          
                            // Use batch update if available
                            if (onItemUpdate) {
                              onItemUpdate(index, calcResult);
                            } else {
                              onFieldChange(index, 'concessionPercent', calcResult.concessionPercent);
                              onFieldChange(index, 'concessionAmount', calcResult.concessionAmount);
                              onFieldChange(index, 'netAmount', calcResult.netAmount);
                            }
                          }}
                          placeholder="-"
                          data-testid={testIds.concessionPercentInput}
                          inputProps={{
                            'aria-label': ARIA_LABELS.concessionPercentInput(item.itemName || item.itemCode),
                          }}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">%</InputAdornment>
                              ),
                            },
                            htmlInput: { min: 0, max: 100, step: 0.01 },
                          }}
                          sx={{
                            flex: '0 0 100px',
                            maxWidth: 100,
                            ...INPUT_FIELD_STYLES,
                          }}
                        />
                      </Box>
                    </TableCell>

                    {/* VAT Amount (%) */}
                    <TableCell>
                      {(item.taxes || []).length > 0 ? (
                        (item.taxes || []).map((tax, idx) => (
                          <Typography key={idx} variant="caption" color="text.secondary" display="block">
                            {tax.name}: {currencySymbol}{(tax.amount || 0).toFixed(2)} ({tax.percent}%)
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('pharmacy_items_table.labels.no_tax')}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Net Amount */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#344054' }}>
                        {currencySymbol}{typeof item.netAmount === 'number' ? item.netAmount.toFixed(2) : '0.00'}
                      </Typography>
                    </TableCell>

                    {/* Pregnancy Class */}
                    <TableCell>
                      {item.pregnancyClass && item.pregnancyClass.length > 1 ? (
                        // Show full pregnancy class description as read-only text
                        <Typography variant="body2" sx={{ color: '#344054', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.pregnancyClass}
                        </Typography>
                      ) : (
                        // Show editable dropdown for single-letter class (A, B, C, D, X)
                        <FormControl fullWidth size="small">
                          <Select
                            value={item.pregnancyClass}
                            onChange={(e) => onFieldChange(index, 'pregnancyClass', e.target.value)}
                            displayEmpty
                            data-testid={testIds.pregnancyClassSelect}
                            inputProps={{
                              'aria-label': ARIA_LABELS.pregnancyClassSelect(item.itemName || item.itemCode),
                            }}
                            renderValue={(selected) => {
                              if (!selected) {
                                return (
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {t('pharmacy_items_table.labels.select_class')}
                                  </Typography>
                                );
                              }
                              return `${t('pharmacy_items_table.labels.class_' + selected.toLowerCase())}`;
                            }}
                            sx={SELECT_FIELD_STYLES}
                            disabled={readOnly && invoiceStatus !== 'dispensed' && !item.packageId}
                          >
                            <MenuItem value="" disabled>{t('pharmacy_items_table.labels.select_class')}</MenuItem>
                            {PREGNANCY_CLASSES.map((cls) => (
                              <MenuItem key={cls} value={cls}>
                                {t(`pharmacy_items_table.labels.class_${cls.toLowerCase()}`)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>

                    {/* Provider */}
                    <TableCell>
                      <Typography variant="body2">{item.provider || '-'}</Typography>
                    </TableCell>
                  </>
                )}

                {/* Delete Action */}
                {showDeleteAction && onRemoveItem && (
                  <TableCell sx={{ borderTopRightRadius: 12, borderBottomRightRadius: 12, width: 64 }}>
                    <Tooltip title={t('pharmacy_items_table.labels.remove')}>
                      <IconButton
                        size="small"
                        onClick={() => !readOnly && onRemoveItem(index)}
                        disabled={readOnly}
                        data-testid={testIds.removeButton}
                        aria-label={ARIA_LABELS.removeButton(item.itemName || item.itemCode)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PharmacyItemsTable;
